import React, { useState } from 'react';
import { gql } from '@apollo/client';
import { useQuery } from 'hooks/apollo';
import { FormattedMessage, injectIntl } from 'react-intl';
import { QUESTION_TYPES, SUB_TYPES } from 'shared/constants/survey';

import { Form, Input, InputNumber, Select, Checkbox, Row } from 'antd';

import EditOptions from './EditOptions';
import EditDependencies from './EditDependencies';

import messages from './messages';
import './edit-question.less';

const QUESTION_CATEGORIES = gql`
  query { questionCategories { id title } }
`;

const { Option } = Select;

const resultsSubString = 'Results - Survey - ';
function getCollections(categories) {
  if (!categories || !categories.length) { return []; }

  const filtered = categories.filter(cat => cat.title.startsWith(resultsSubString));
  return filtered.map(cat => ({
    id: cat.id,
    title: cat.title.replace(resultsSubString, ''),
  }));
}

function getQuestionTags(categories) {
  if (!categories || !categories.length) { return []; }

  return categories.filter(cat => !cat.title.startsWith(resultsSubString));
}

const EditQuestion = ({
  intl: { formatMessage: $t },
  question, dependencies, dependencyOperator,
  surveyQuestions = [],
  form, onSubmit,
}) => {
  const { loading: loadingCategories, data: { questionCategories = [] } = {} } = useQuery(QUESTION_CATEGORIES);
  const collections = getCollections(questionCategories);
  const questionTags = getQuestionTags(questionCategories);

  const [showRowCount, setShowRowCount] = useState(question.settings.subType === SUB_TYPES.TEXTAREA);
  const onFieldTypeChanges = (value) => setShowRowCount(value === SUB_TYPES.TEXTAREA);

  const { type } = question.settings;

  const showOptions = type === QUESTION_TYPES.CHECKBOX || type === QUESTION_TYPES.RADIO;

  const { subType, required, random, lockLastOption, rows, min, max, email } = question.settings;
  const questionTitle = !question.title && type === QUESTION_TYPES.NPS
    ? $t(messages.defaultNPStitle)
    : question.title;

  const initialValues = {
    title: questionTitle,
    description: question.description,
    resultsCollection: getCollections(question.categories).map(cat => cat.id),
    questionTags: getQuestionTags(question.categories).map(cat => cat.id),
    options: question.options,
    dependencies,
    dependencyOperator: dependencyOperator || 'AND',
    subType: subType || SUB_TYPES.TEXTAREA,
    required,
    email,
    rows,
    random,
    lockLastOption,
    min,
    max,
  };

  return (
    <Form layout="vertical" form={form} onFinish={onSubmit} initialValues={initialValues}>
      <Row>
        <div className="edit-question__left">
          <Form.Item
            name="title"
            label={<FormattedMessage {...messages.questionTitle} />}
            rules={[{ required: true, message: $t(messages.questionTitleRequired) }]}
          >
            <Input placeholder={$t(messages.questionTitlePlaceholder)} />
          </Form.Item>

          <Form.Item name="description" label={<FormattedMessage {...messages.questionDescription} />}>
            <Input placeholder={$t(messages.questionDescriptionPlaceholder)} />
          </Form.Item>

          <Form.Item name="required" valuePropName="checked">
            <Checkbox><FormattedMessage {...messages.required} /></Checkbox>
          </Form.Item>

          {type === QUESTION_TYPES.FIELD && (
            <Row>
              <Form.Item name="subType" label={<FormattedMessage {...messages.subType} />}>
                <Select onChange={onFieldTypeChanges}>
                  {Object.values(SUB_TYPES).map(st => (
                    <Option key={st} value={st}><FormattedMessage {...messages[st]} /></Option>
                  ))}
                </Select>
              </Form.Item>
              {showRowCount && (
                <Form.Item style={{ marginLeft: 32 }} name="rows" label={<FormattedMessage {...messages.rowCount} />}>
                  <InputNumber />
                </Form.Item>
              )}
            </Row>
          )}
          {type === QUESTION_TYPES.CONTACT && (
            <Form.Item name="email" label="Contact Email">
              <Input type="email" />
            </Form.Item>
          )}

          <Form.Item
            name="resultsCollection"
            label={<FormattedMessage {...messages.resultsCollection} />}
            rules={[{ required: true, message: $t(messages.resultsCollectionRequired) }]}
          >
            <Select
              allowClear
              loading={loadingCategories}
              placeholder={$t(messages.resultsCollectionPlaceholder)}
            >
              {collections.map(cat => <Option key={cat.id} value={cat.id}>{cat.title}</Option>)}
            </Select>
          </Form.Item>

          <Form.Item name="questionTags" label={<FormattedMessage {...messages.questionTags} />}>
            <Select
              loading={loadingCategories}
              placeholder={$t(messages.questionTagsPlaceholder)}
              mode="multiple"
              allowClear
            >
              {questionTags.map(cat => <Option key={cat.id} value={cat.id}>{cat.title}</Option>)}
            </Select>
          </Form.Item>

          <EditDependencies questions={surveyQuestions} />
        </div>

        {showOptions && (
          <div className="edit-question__right">
            <Form.Item name="options" valuePropName="options" label={<FormattedMessage {...messages.options} />}>
              <EditOptions />
            </Form.Item>
            <Form.Item name="random" valuePropName="checked">
              <Checkbox><FormattedMessage {...messages.randomize} /></Checkbox>
            </Form.Item>
            <Form.Item shouldUpdate={(prevValues, curValues) => prevValues.random !== curValues.random}>
              {({ getFieldValue }) => (
                <Form.Item
                  name="lockLastOption"
                  valuePropName="checked"
                >
                  <Checkbox disabled={!getFieldValue('random')}><FormattedMessage {...messages.lockLastOption} /></Checkbox>
                </Form.Item>
              )}
            </Form.Item>

            {type === QUESTION_TYPES.CHECKBOX && (
            <Row justify="space-between">
              <Form.Item name="min" label={<FormattedMessage {...messages.minAnswers} />}>
                <InputNumber />
              </Form.Item>
              <Form.Item name="max" label={<FormattedMessage {...messages.maxAnswers} />}>
                <InputNumber />
              </Form.Item>
            </Row>
            )}
          </div>
        )}
      </Row>
    </Form>
  );
};

export default injectIntl(EditQuestion);
